import { Classes, Radio, RadioProps } from "@blueprintjs/core";
import { rgba } from "polished";
import React from "react";
import styled from "styled-components";

export type HexRadioProps = RadioProps & {
  small?: boolean;
};

/**
 * https://blueprintjs.com/docs/#core/components/radio
 */
const StyledRadio = styled(Radio)<{
  $small?: boolean;
}>`
  &&&& {
    margin: 0;

    color: ${({ theme }) => theme.fontColor.DEFAULT};
    font-size: ${({ theme }) => theme.fontSize.DEFAULT};

    ${({ $small, theme }) => $small && `font-size: ${theme.fontSize.SMALL};`}

    &:hover > .${Classes.CONTROL_INDICATOR} {
      background-color: ${({ theme }) => theme.hoverColor};
    }

    /* BOX STYLES */
    input ~ .${Classes.CONTROL_INDICATOR} {
      position: relative;
      margin-right: 7px;
      font-size: ${({ $small, theme }) => $small && theme.fontSize.DEFAULT};

      background-color: ${({ theme }) => theme.backgroundColor.DEFAULT};

      border-radius: 50%;
      background-image: none;
      box-shadow: ${({ theme }) => theme.boxShadow.BUTTON};

      transition:
        background-color ${({ theme }) => theme.animation.duration}
          ${({ theme }) => theme.animation.easing},
        box-shadow ${({ theme }) => theme.animation.duration}
          ${({ theme }) => theme.animation.easing};

      &::before {
        position: absolute;
        ${({ $small }) =>
          $small
            ? `left: 4px;
        top: 4px`
            : `left: 5px;
        top: 5px`};
        background: ${({ theme }) => theme.backgroundColor.DEFAULT};
        opacity: 0;
        box-shadow: 0 0 0 1px ${({ theme }) => theme.intent.PRIMARY};
        width: 6px;
        height: 6px;
        margin: 0;
        border-radius: 50%;
        transition: opacity ${({ theme }) => theme.animation.duration}
          ${({ theme }) => theme.animation.easing};
      }
    }

    /* CHECKED */
    input:checked ~ .${Classes.CONTROL_INDICATOR} {
      box-shadow: inset 0 0 0 1px ${({ theme }) => theme.intent.PRIMARY};

      &::before {
        opacity: 1;
        background-color: ${({ theme }) => rgba(theme.intent.PRIMARY, 0.15)};
      }
    }

    /* HOVERING, NOT DISABLED */
    &:not(.${Classes.DISABLED}):hover {
      /* NOT CHECKED */
      input ~ .${Classes.CONTROL_INDICATOR} {
        background-color: ${({ theme }) => theme.hoverColor};
      }

      /* CHECKED */
      input:checked ~ .${Classes.CONTROL_INDICATOR} {
        background-color: ${({ theme }) => rgba(theme.intent.PRIMARY, 0.25)};
      }
    }

    /* ACTIVE, NOT DISABLED */
    &:not(.${Classes.DISABLED}):active {
      /* NOT CHECKED */
      input ~ .${Classes.CONTROL_INDICATOR} {
        box-shadow: inset 0 0 0 1px ${({ theme }) => theme.borderColor.DEFAULT};
      }

      /* CHECKED */
      input:checked ~ .${Classes.CONTROL_INDICATOR} {
        background-color: ${({ theme }) => rgba(theme.intent.PRIMARY, 0.3)};
        box-shadow: inset 0 0 0 1px ${({ theme }) => theme.intent.PRIMARY};
      }
    }

    /* DISABLED */
    &.${Classes.DISABLED} {
      ${({ theme }) => theme.disabledState}
    }
  }
`;

// eslint-disable-next-line tree-shaking/no-side-effects-in-initialization
export const HexRadio = React.forwardRef<
  HTMLLabelElement,
  Omit<HexRadioProps, "ref">
>(function HexRadio({ small, ...props }, ref) {
  return <StyledRadio $small={small} {...props} ref={ref} />;
});

// Set display name to be BP component name so that it works in radio groups
HexRadio.displayName = Radio.displayName;
